import { useEffect, useMemo, useState } from 'react';
import {
  addDays,
  civilDateOf,
  classifyWaste,
  isoWeekNumber,
  localMidnightIso,
  startOfWeek,
  todayIn,
  type CalendarEvent,
  type WasteKind,
} from '@frisk/shared';
import { kicker, memberColor, t } from '../theme';
import { WasteIcon } from '../components/widgets/WasteIcon';
import { useStore, useWasteFeedIds } from '../store';

const DOW = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

const shortDate = (d: string) => `${Number(d.slice(8, 10))}. ${MONTHS[Number(d.slice(5, 7)) - 1]}`;

export function Calendar() {
  const events = useStore((s) => s.events);
  const members = useStore((s) => s.members);
  const loadEvents = useStore((s) => s.loadEvents);
  const wasteFeeds = useWasteFeedIds();

  const today = todayIn(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [anchor, setAnchor] = useState(today);
  const [view, setView] = useState<'week' | 'day'>('week');
  const [memberFilter, setMemberFilter] = useState<string | null>(null);
  const [showWaste, setShowWaste] = useState(true);

  const days = useMemo(() => {
    if (view === 'day') return [anchor];
    const monday = startOfWeek(anchor);
    return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  }, [anchor, view]);

  // Refetch whenever the visible window moves; the store keeps the range so an
  // SSE push can reload exactly what is on screen.
  useEffect(() => {
    const first = days[0]!;
    const afterLast = addDays(days[days.length - 1]!, 1);
    void loadEvents(localMidnightIso(first), localMidnightIso(afterLast));
  }, [days, loadEvents]);

  const isWaste = (event: CalendarEvent) => Boolean(event.feedId && wasteFeeds.has(event.feedId));

  /**
   * Bin collections per day, kept apart from appointments.
   *
   * They qualify a day rather than occupying a time, so they are drawn as a
   * marker strip above the column instead of competing with Ballett and
   * Zahnarzt for the same card slot. Member filtering does not apply — the bin
   * is nobody's in particular.
   */
  const wasteByDay = useMemo(() => {
    const map = new Map<string, WasteKind[]>();
    if (!showWaste) return map;
    for (const event of events) {
      if (!isWaste(event)) continue;
      const day = civilDateOf(event.startsAt);
      const kinds = map.get(day) ?? [];
      const kind = classifyWaste(event.title);
      if (!kinds.some((k) => k.type === kind.type)) kinds.push(kind);
      map.set(day, kinds);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, wasteFeeds, showWaste]);

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const event of events) {
      if (isWaste(event)) continue;
      if (memberFilter && event.memberId !== memberFilter) continue;
      const day = civilDateOf(event.startsAt);
      const list = map.get(day);
      if (list) list.push(event);
      else map.set(day, [event]);
    }
    // All-day entries head the column, then timed events in order.
    for (const list of map.values()) {
      list.sort((a, b) =>
        a.allDay === b.allDay ? a.startsAt.localeCompare(b.startsAt) : a.allDay ? -1 : 1,
      );
    }
    return map;
  }, [events, memberFilter]);

  const step = (direction: 1 | -1) => setAnchor((a) => addDays(a, direction * (view === 'week' ? 7 : 1)));

  const memberOf = (id: string | null) => members.find((m) => m.id === id) ?? null;

  const monthLabel = (() => {
    const first = days[0]!;
    const last = days[days.length - 1]!;
    const sameMonth = first.slice(0, 7) === last.slice(0, 7);
    const month = (d: string) => `${MONTHS[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}`;
    return sameMonth ? month(first) : `${MONTHS[Number(first.slice(5, 7)) - 1]}/${month(last)}`;
  })();

  const subLabel =
    view === 'week'
      ? `KW ${isoWeekNumber(days[0]!)} · ${Number(days[0]!.slice(8, 10))}.–${shortDate(days[6]!)}`.toUpperCase()
      : `${DOW[(new Date(anchor + 'T00:00:00Z').getUTCDay() + 6) % 7]} · ${shortDate(anchor)}`.toUpperCase();

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '34px 38px 30px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 22, flex: 'none' }}>
          <div>
            <div style={kicker}>{subLabel}</div>
            <div style={{ fontSize: 44, fontWeight: 500, letterSpacing: '-0.045em', marginTop: 6, whiteSpace: 'nowrap' }}>
              {monthLabel}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, paddingBottom: 6 }}>
            <div className="press" onClick={() => step(-1)} style={{ width: 58, height: 58, borderRadius: 999, background: 'oklch(0.23 0.012 60)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>
              ‹
            </div>
            <div className="press" onClick={() => step(1)} style={{ width: 58, height: 58, borderRadius: 999, background: 'oklch(0.23 0.012 60)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>
              ›
            </div>
            {anchor !== today && (
              <div className="press" onClick={() => setAnchor(today)} style={{ height: 58, padding: '0 20px', borderRadius: 999, background: 'oklch(0.23 0.012 60)', display: 'flex', alignItems: 'center', fontSize: 19, fontWeight: 500, whiteSpace: 'nowrap' }}>
                Heute
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', padding: 4, gap: 4, background: 'oklch(0.23 0.012 60)', borderRadius: 14 }}>
            {(['day', 'week'] as const).map((mode) => (
              <div
                key={mode}
                onClick={() => setView(mode)}
                style={{
                  cursor: 'pointer',
                  padding: '11px 20px',
                  borderRadius: 11,
                  fontSize: 19,
                  fontWeight: 500,
                  background: view === mode ? 'oklch(0.32 0.012 60)' : 'transparent',
                  color: view === mode ? t.ink : 'oklch(0.68 0.015 70)',
                }}
              >
                {mode === 'day' ? 'Tag' : 'Woche'}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* member filter — "Alle" is an explicit chip, not the absence of one */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
        <div
          className="press"
          onClick={() => setMemberFilter(null)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 19,
            fontWeight: 500,
            padding: '11px 18px',
            borderRadius: 999,
            background: memberFilter === null ? 'oklch(0.3 0.012 60)' : t.surface,
            color: memberFilter === null ? t.ink : 'oklch(0.62 0.015 70)',
            border: memberFilter === null ? `1px solid ${t.accent}` : t.hairline,
          }}
        >
          Alle
        </div>
        {members.map((m) => {
          const on = memberFilter === m.id;
          return (
            <div
              key={m.id}
              className="press"
              onClick={() => setMemberFilter(on ? null : m.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontSize: 19,
                fontWeight: 500,
                padding: '11px 18px',
                borderRadius: 999,
                background: on ? 'oklch(0.3 0.012 60)' : t.surface,
                color: on || memberFilter === null ? 'oklch(0.95 0.01 80)' : 'oklch(0.55 0.015 70)',
                border: on ? `1px solid ${memberColor(m.hue)}` : t.hairline,
              }}
            >
              <div style={{ width: 12, height: 12, borderRadius: 999, background: memberColor(m.hue) }} />
              {m.name}
            </div>
          );
        })}

        {/* A layer, not a member: it sits apart because the bin belongs to
            nobody, and it only appears once a waste feed exists. */}
        {wasteFeeds.size > 0 && (
          <>
            <div style={{ width: 1, height: 30, background: 'oklch(1 0 0 / 0.1)', margin: '0 4px' }} />
            <div
              className="press"
              onClick={() => setShowWaste((v) => !v)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                fontSize: 19,
                fontWeight: 500,
                padding: '11px 18px',
                borderRadius: 999,
                background: showWaste ? 'oklch(0.3 0.012 60)' : t.surface,
                color: showWaste ? 'oklch(0.95 0.01 80)' : 'oklch(0.55 0.015 70)',
                border: showWaste ? '1px solid oklch(0.70 0.02 70)' : t.hairline,
              }}
            >
              <WasteIcon size={18} />
              Müll
            </div>
          </>
        )}
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          marginTop: 18,
          borderRadius: 26,
          background: t.surfaceSunken,
          border: t.hairline,
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: `repeat(${days.length}, 1fr)`,
        }}
      >
        {days.map((day, index) => {
          const isToday = day === today;
          const dayEvents = byDay.get(day) ?? [];
          return (
            <div
              key={day}
              style={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                borderLeft: index === 0 ? 'none' : '1px solid oklch(1 0 0 / 0.06)',
                background: isToday ? 'oklch(0.235 0.012 60)' : 'transparent',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  padding: '16px 14px 12px',
                  borderBottom: '1px solid oklch(1 0 0 / 0.06)',
                }}
              >
                <span style={{ fontFamily: t.mono, fontSize: 13, letterSpacing: '0.12em', color: isToday ? t.accent : t.inkFaint }}>
                  {DOW[(new Date(day + 'T00:00:00Z').getUTCDay() + 6) % 7]}
                </span>
                <span style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em', color: isToday ? t.accent : t.ink }}>
                  {Number(day.slice(8, 10))}
                </span>
              </div>

              {/* Bin collections: a marker strip, not a card. Shape carries
                  the category, colour only says which bin. */}
              {(wasteByDay.get(day) ?? []).length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    flexWrap: 'wrap',
                    padding: '8px 12px',
                    borderBottom: '1px solid oklch(1 0 0 / 0.06)',
                  }}
                >
                  {(wasteByDay.get(day) ?? []).map((kind) => (
                    <div
                      key={kind.type}
                      title={kind.label}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, color: kind.colour, minWidth: 0 }}
                    >
                      <WasteIcon size={16} />
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: kind.colour,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {kind.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {dayEvents.map((event) => {
                  const owner = memberOf(event.memberId);
                  const colour = owner ? memberColor(owner.hue) : 'oklch(0.72 0.02 80)';
                  return (
                    <div
                      key={event.id}
                      style={{
                        borderRadius: 14,
                        background: 'oklch(0.27 0.012 60)',
                        borderLeft: `3px solid ${colour}`,
                        padding: '10px 10px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                      }}
                    >
                      <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.1em', color: 'oklch(0.7 0.015 70)' }}>
                        {/* An all-day entry has no meaningful clock time; showing
                            00:00 next to real appointments reads as an error. */}
                        {event.allDay
                          ? 'GANZTÄGIG'
                          : new Date(event.startsAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <div
                        style={{
                          fontSize: view === 'day' ? 22 : 16,
                          fontWeight: 600,
                          letterSpacing: '-0.01em',
                          lineHeight: 1.2,
                          textWrap: 'pretty',
                          // Week columns are ~130px; hyphenate German compounds
                          // rather than snapping words at an arbitrary letter.
                          hyphens: 'auto',
                          overflowWrap: 'break-word',
                        }}
                      >
                        {event.title}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 14, color: 'oklch(0.72 0.015 70)' }}>
                        <div style={{ width: 8, height: 8, borderRadius: 999, background: colour }} />
                        {owner?.name ?? 'Alle'}
                      </div>
                    </div>
                  );
                })}
                {dayEvents.length === 0 && (
                  <div style={{ fontSize: 15, color: 'oklch(0.5 0.012 60)', padding: '6px 2px' }}>—</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
